import { AutomatedOption, NourishmentOption, PrecisionOption, ReferralSource } from '@core/models/account';
import { GeoJsonType } from '@core/models/address';
import { OnboardingFlow, OnboardingState, OnboardingStep } from '@core/models/onboarding';
import { stepsForFlow } from '@core/onboarding/onboarding-machine';

const STEP_MOCKS: Partial<Record<OnboardingStep, Partial<OnboardingState>>> = {
  [OnboardingStep.Learning]: {
    completedLearning: true,
    completedSteps: [OnboardingStep.Learning]
  },
  [OnboardingStep.Availability]: {
    availabilityAddress: {
      name: 'Home',
      street: '123 Main Street',
      city: 'Austin',
      state: 'TX',
      zipCode: '78701',
      country: 'US',
      isDefault: true,
      geometry: { type: GeoJsonType.Point, coordinates: [-97.7431, 30.2672] }
    },
    availabilityResult: true,
    completedSteps: [OnboardingStep.Learning, OnboardingStep.Availability]
  },
  [OnboardingStep.AvailabilityResult]: {
    availabilityResultSeen: true,
    completedSteps: [OnboardingStep.Learning, OnboardingStep.Availability, OnboardingStep.AvailabilityResult]
  },
  [OnboardingStep.AboutYou]: {
    aboutYouForm: {
      preferredName: 'Test',
      firstName: 'Test',
      lastName: 'User',
      dateOfBirth: '1990-01-15',
      email: 'test@youtopia.com',
      phone: '5125551234',
      referralSource: ReferralSource.Friend,
      referralSourceAdditional: null
    },
    userProfileCreated: true,
    completedSteps: [
      OnboardingStep.Learning,
      OnboardingStep.Availability,
      OnboardingStep.AvailabilityResult,
      OnboardingStep.AboutYou
    ]
  },
  [OnboardingStep.RightPlace]: {
    rightPlaceSelections: ['health', 'nutrition', 'wellness'],
    rightPlaceCongratsSeen: true,
    completedSteps: [
      OnboardingStep.Learning,
      OnboardingStep.Availability,
      OnboardingStep.AvailabilityResult,
      OnboardingStep.AboutYou,
      OnboardingStep.RightPlace
    ]
  },
  [OnboardingStep.PlanSelection]: {
    planSelectionForm: {
      packageId: '30000000-0000-0000-0000-000000000005',
      automatedOption: AutomatedOption.Automated,
      precisionOption: PrecisionOption.Thrive,
      nourishmentOption: NourishmentOption.Boost
    },
    planSelectionCompletedSections: 3,
    cartItemsAdded: true,
    completedSteps: [
      OnboardingStep.Learning,
      OnboardingStep.Availability,
      OnboardingStep.AvailabilityResult,
      OnboardingStep.AboutYou,
      OnboardingStep.RightPlace,
      OnboardingStep.PlanSelection
    ]
  },
  [OnboardingStep.AddMembers]: {
    addMemberForms: [],
    completedSteps: [
      OnboardingStep.Learning,
      OnboardingStep.Availability,
      OnboardingStep.AvailabilityResult,
      OnboardingStep.AboutYou,
      OnboardingStep.RightPlace,
      OnboardingStep.PlanSelection,
      OnboardingStep.AddMembers
    ]
  },
  [OnboardingStep.Agreements]: {
    agreementsAccepted: true,
    completedSteps: [
      OnboardingStep.Learning,
      OnboardingStep.Availability,
      OnboardingStep.AvailabilityResult,
      OnboardingStep.AboutYou,
      OnboardingStep.RightPlace,
      OnboardingStep.PlanSelection,
      OnboardingStep.AddMembers,
      OnboardingStep.Agreements
    ]
  }
};

export function getMockStateForStep(targetStep: OnboardingStep): Partial<OnboardingState> {
  // Steps are ordered by their position in the flow array, so merge the mocks of every step that
  // precedes the target.
  const standardSteps = stepsForFlow(OnboardingFlow.Standard);
  const targetIndex = standardSteps.findIndex((d) => d.step === targetStep);
  if (targetIndex < 0) return {};

  let merged: Partial<OnboardingState> = {};
  for (let i = 0; i < targetIndex; i++) {
    const mock = STEP_MOCKS[standardSteps[i].step];
    if (mock) merged = { ...merged, ...mock };
  }
  return merged;
}
